/* @flow */

import * as React from 'react';
import { useSelector } from 'react-redux';
import { v4 as uuidv4 } from 'uuid';
import { getAuthenticationToken } from 'src/shared/app/authentication/selector/base/appAuthenticationBaseSelectors';
import useRefState from 'src/shared/app/base/hook/useRefState';

export default function useSocket(
  url: ?string,
  handleMessage: (Object) => void,
  handleInit?: () => void,
  handleDestroy?: () => void,
) {
  const [loadingId, setLoadingId, loadingIdRef] = useRefState<string | boolean>(
    false,
  );
  const [isConnecting, setIsConnecting] = React.useState<boolean>(true);

  const handleLoadingId = ({ uuid }) => {
    // Uses ref because it is triggered by an event listener and is unsynced with the compoenent state
    if (loadingIdRef.current && uuid && uuid === loadingIdRef.current) {
      setLoadingId(false);
    }
  };
  const token = useSelector(getAuthenticationToken);

  const socket = React.useRef<WebSocket | null>(null);

  let delay = 1000;
  let timeoutId;

  /*
   |----------------------------------------------------------------------------
   | Effects
   |----------------------------------------------------------------------------
   */

  React.useEffect(() => {
    if (url && token) {
      resetSocket();
    }

    return () => {
      closeSocket();
      destroySocket();

      if (timeoutId) {
        window.clearTimeout(timeoutId);
      }
    };
  }, [url, token]);

  /*
   |----------------------------------------------------------------------------
   | Event Handlers
   |----------------------------------------------------------------------------
   */

  const onMessage = ({ data }: Object) => {
    const json = JSON.parse(String(data));
    handleLoadingId(json);
    handleMessage(json);
  };

  const onClose = ({ wasClean }: Object) => {
    destroySocket();
    if (!wasClean) {
      setTimeout(() => {
        initSocket();
      }, Math.min(delay, 300000));
    }
    delay = delay * 2;
  };

  const onOpen = () => {
    setIsConnecting(false);
  };

  const onError = () => {};

  /*
   |----------------------------------------------------------------------------
   | Functions
   |----------------------------------------------------------------------------
   */

  const initSocket = () => {
    if (!socket.current && url) {
      timeoutId = window.setTimeout(() => {
        socket.current = new WebSocket(url, ['Token', `${token}`]);
        if (socket.current) {
          socket.current.addEventListener('message', onMessage);
          // $FlowIssue
          socket.current.addEventListener('close', onClose);
          // $FlowIssue
          socket.current.addEventListener('error', onError);
          // $FlowIssue
          socket.current.addEventListener('open', onOpen);
        }

        setLoadingId(null);
        if (handleInit) {
          handleInit();
        }
        // FIXME: timeout is necessary because of BE issue
      }, 1000);
    }
  };

  const closeSocket = () => {
    if (socket.current) {
      socket.current.close();
    }
  };

  const resetSocket = () => {
    closeSocket();
    initSocket();
  };

  const destroySocket = () => {
    if (socket.current) {
      socket.current.removeEventListener('message', onMessage);
      // $FlowIssue
      socket.current.removeEventListener('close', onClose);
      // $FlowIssue
      socket.current.removeEventListener('error', onError);
      // $FlowIssue
      socket.current.removeEventListener('open', onOpen);

      socket.current = null;

      if (handleDestroy) {
        handleDestroy();
      }
    }
  };

  const generateUuid = (namespace) => `${namespace}-${uuidv4()}`;

  const handleSend = (
    namespace: string,
    data: Object,
    ignoreLoading: ?boolean,
  ) => {
    const uuid = generateUuid(namespace);
    if (!ignoreLoading) {
      setLoadingId(uuid);
    }

    return socket.current?.send(JSON.stringify({ ...data, uuid }));
  };

  return {
    send: handleSend,
    isLoading: !!loadingId || isConnecting,
    isConnecting,
  };
}
